/* Discounts are saved by Shopify. All displayed prices come from a fresh Liquid render. */
(() => {
  if (customElements.get('med-bag-discount')) return;

  class MedicalBagDiscount extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.querySelector('form').hidden = false;
      this.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.update();
      });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-code]');
        if (button) this.update(button.dataset.removeCode);
      });
    }

    codes() {
      return [...this.querySelectorAll('[data-remove-code]')].map((button) => button.dataset.removeCode);
    }

    message(text, error = false, refresh = false) {
      const status = this.querySelector('[role=status]');
      status.textContent = text;
      status.toggleAttribute('data-error', error);
      if (refresh) {
        const link = document.createElement('a');
        link.href = window.location.href;
        link.textContent = 'Refresh shopping bag';
        status.append(document.createElement('br'), link);
      }
    }

    renderCodes(codes) {
      const list = this.querySelector('.med-bag__codes');
      list.replaceChildren();
      for (const code of [...new Set(codes)]) {
        const row = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = code;
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.removeCode = code;
        button.setAttribute('aria-label', `Remove discount ${code}`);
        button.textContent = '×';
        row.append(label, button);
        list.append(row);
      }
      list.hidden = codes.length === 0;
    }

    async request(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...options,
      });
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    }

    async update(removeCode) {
      if (this.busy) return;
      const removing = typeof removeCode === 'string';
      const input = this.querySelector('input[name=discount]');
      const code = removing ? removeCode : input.value.trim();
      if (!code || code.includes(',')) {
        this.message('Enter one discount code at a time.', true);
        input.focus();
        return;
      }
      const bag = this.closest('.med-bag');
      if ([...bag.querySelectorAll('med-cart-editor')].some((editor) => editor.busy)) {
        this.message('Please let your engraving finish saving, then apply your code.', true);
        return;
      }

      // Keep any unsaved engraving edits while Shopify refreshes prices and line keys.
      const drafts = [...bag.querySelectorAll('med-cart-editor')].map((editor) => ({
        bundle: JSON.parse(editor.dataset.properties || '{}')._med_bundle || editor.dataset.key,
        value: editor.querySelector('textarea').value,
        checked: editor.querySelector('input[type=checkbox]').checked,
        open: editor.querySelector('details').open,
      }));
      const note = bag.querySelector('textarea[name=note]');
      const noteValue = note?.value;
      const noteOpen = note?.closest('details')?.open;
      const controls = [...bag.querySelectorAll('button,input,textarea')].map((node) => [node, node.disabled]);
      const items = bag.querySelector('.med-bag__items');
      let posted = false;
      this.busy = true;
      bag.setAttribute('aria-busy', 'true');
      if (items) items.inert = true;
      controls.forEach(([node]) => { node.disabled = true; });
      this.message(removing ? 'Removing code…' : 'Applying code…');

      try {
        // Read current server codes first, including shipping codes with no cart allocation yet.
        const current = await this.request(`${this.dataset.cartUrl}.js`);
        if (!Array.isArray(current.items)) throw new Error('Invalid cart');
        const existing = Array.isArray(current.discount_codes)
          ? current.discount_codes.filter((discount) => discount.applicable !== false).map((discount) => discount.code)
          : this.codes();
        const alreadyApplied = existing.some((existingCode) => existingCode.toLowerCase() === code.toLowerCase());
        const requested = existing.filter((existingCode) => existingCode.toLowerCase() !== code.toLowerCase());
        if (!removing) requested.push(code);
        const body = {
          discount: requested.join(','),
          sections: [this.dataset.sectionId],
          sections_url: window.location.pathname + window.location.search,
        };
        posted = true;
        const cart = await this.request(this.dataset.updateUrl, { method: 'POST', body: JSON.stringify(body) });
        if (!Array.isArray(cart.items)) throw new Error('Invalid updated cart');
        let html = cart.sections?.[this.dataset.sectionId];
        if (!html) {
          const url = new URL(window.location.href);
          url.searchParams.set('sections', this.dataset.sectionId);
          const sections = await this.request(url.pathname + url.search);
          html = sections[this.dataset.sectionId];
        }
        const parsed = new DOMParser().parseFromString(html || '', 'text/html');
        const updatedBag = parsed.querySelector('.med-bag');
        const updatedDiscount = updatedBag?.querySelector('med-bag-discount');
        if (!updatedBag || !updatedDiscount || !updatedBag.querySelector('.med-bag__grand-total')) {
          throw new Error('Cart could not be rendered');
        }
        const serverCodes = Array.isArray(cart.discount_codes)
          ? cart.discount_codes.filter((discount) => discount.applicable === true).map((discount) => discount.code)
          : [...updatedDiscount.querySelectorAll('[data-remove-code]')].map((button) => button.dataset.removeCode);
        const applied = serverCodes.some((serverCode) => serverCode.toLowerCase() === code.toLowerCase());
        bag.replaceWith(updatedBag);
        // Connected custom elements now use Shopify's current line keys and properties.
        for (const editor of updatedBag.querySelectorAll('med-cart-editor')) {
          const bundle = JSON.parse(editor.dataset.properties || '{}')._med_bundle || editor.dataset.key;
          const draft = drafts.find((entry) => entry.bundle === bundle);
          if (draft) {
            editor.querySelector('textarea').value = draft.value;
            editor.querySelector('input[type=checkbox]').checked = draft.checked;
            editor.querySelector('details').open = draft.open;
          }
        }
        const updatedNote = updatedBag.querySelector('textarea[name=note]');
        if (updatedNote && noteValue !== undefined) {
          updatedNote.value = noteValue;
          updatedNote.closest('details').open = !!noteOpen;
        }
        updatedDiscount.renderCodes(serverCodes);
        if (removing) {
          updatedDiscount.message(applied ? 'This code could not be removed. Please try again.' : 'Discount code removed.', applied);
        } else if (applied) {
          updatedDiscount.message(alreadyApplied
            ? updatedDiscount.dataset.alreadyApplied
            : cart.total_price < current.total_price
            ? updatedDiscount.dataset.applied
            : 'Code added. Any eligible savings are shown below; shipping offers are confirmed at checkout.');
        } else {
          updatedDiscount.querySelector('input[name=discount]').value = code;
          updatedDiscount.message('This code isn’t available for this order. Check the code and its terms, or try another.', true);
        }
        updatedDiscount.querySelector('[role=status]').focus({ preventScroll: true });
        document.dispatchEvent(new CustomEvent('discount:update', {
          bubbles: true, detail: { resource: cart, sourceId: this.dataset.sectionId },
        }));
      } catch (_) {
        this.message(posted
          ? 'We couldn’t confirm the updated total. Refresh your shopping bag before checking out.'
          : 'We couldn’t connect. Please try again.', true, posted);
        if (posted) bag.dataset.discountUnconfirmed = 'true';
      } finally {
        this.busy = false;
        bag.removeAttribute('aria-busy');
        if (items) items.inert = false;
        controls.forEach(([node, disabled]) => { node.disabled = disabled; });
        if (bag.dataset.discountUnconfirmed) {
          const checkout = bag.querySelector('[name=checkout]');
          if (checkout) checkout.disabled = true;
        }
      }
    }
  }
  customElements.define('med-bag-discount', MedicalBagDiscount);
})();
